import React, { useState } from 'react';
import {
  FiChevronUp,
  FiChevronDown,
  FiInfo,
  FiX,
  FiPaperclip,
  FiFileText,
  FiFile,
  FiAlertCircle,
} from 'react-icons/fi';
import { useReviewerContext } from '../../context/ReviewerContext';
import { questionnairesApi } from '../../services/api';

const confidenceLevels = ['Very Low', 'Low', 'Moderate', 'High', 'Very High'];

const QuestionCard = ({ question }) => {
  // ─── Context ───────────────────────────────────────────────────────────────
  const {
    activeQnr,
    proposal,
    sourceAnswer, // Get sourceAnswer from context
    toggleSelect,
    handleApprove,
    handleReject,
    handleUndo,
    updateQuestion,
    handleOptionChange,
    handleConfidenceChange,
    handleSaveAnswer,
    handleCloseUploadPanel,
  } = useReviewerContext();

  // ─── States ────────────────────────────────────────────────────────────────
  const [showDetails, setShowDetails] = useState(false);
  const [detailTab, setDetailTab] = useState('Cross-Walk');
  const [uploadFile, setUploadFile] = useState(null);

  // ─── Derived Values ────────────────────────────────────────────────────────
  const q = question;
  const isReviewed = q.reviewStatus !== null;
  const isApproved = q.reviewStatus === 'approved';
  const isRejected = q.reviewStatus === 'rejected';
  const hasPendingChanges = q.pendingAnswer !== null || q.pendingConfidence !== null;

  // Debug: Log proposal matching for this question
  console.log(`QUESTION ${q.id}: Looking for proposal with target_question_id=${q.id}`);
  console.log(`QUESTION ${q.id}: Available proposals:`, proposal?.map(p => p.target_question_id));

  const rec = proposal?.find(r => r.target_question_id === q.id);
  
  if (rec) {
    console.log(`QUESTION ${q.id}: Found matching proposal:`, rec);
  } else {
    console.log(`QUESTION ${q.id}: No matching proposal found`);
  }

  // Compute match type for this question (used in options and answer row)
  const sourceAns = sourceAnswer?.find(sa => sa.answer_id === rec?.source_answer_id);
  let matchType = null;
  let matchedOptionIdx = -1;

  if (sourceAns?.answer_text) {
    const sourceText = sourceAns.answer_text.toLowerCase().trim();
    
    // Check all options for match
    q.options.forEach((opt, idx) => {
      const optText = opt.toLowerCase().trim();
      if (sourceText === optText) {
        matchType = "Exact Match";
        matchedOptionIdx = idx;
      } else if (!matchType && (optText.includes(sourceText) || sourceText.includes(optText))) {
        matchType = "Partial Match";
        matchedOptionIdx = idx;
      }
    });

    // If source answer exists but no match found
    if (!matchType) {
      matchType = "No Match";
    }
  }

  const borderColor = isApproved
    ? 'border-[ #0076b570]'
    : isRejected
      ? 'border-[ #f4812e55]'
      : q.selected
        ? 'border-[ #f4812e63]'
        : 'border-[ #91919144]';


        function formatRationale(input) {
  let text = input ?? '';

  // remove "Details:"
  text = text.replace(/Details:/gi, '');

  // add newline after :
  text = text.replace(/:/g, ':\n');

  // bold from start → first :
  text = text.replace(/^([^:]+):/, '<b>$1:</b>');

  // bold from . → next :
  text = text.replace(/\.([^:]+):/g, '.<b>$1:</b>');

  return text;
}

  const handleOpenDocument = async () => {
    if (!q.answerId || !activeQnr) return;
    try {
      const result = await questionnairesApi.getDocumentUrl(activeQnr, q.answerId);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open document', err);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={` border-2 ${borderColor} p-4 transition-all relative overflow-hidden`}>
      {/* Question header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex items-start gap-2 mt-0.5">
            <span className="text-xs font-semibold text-[#919191] flex items-center gap-1">
              Question{' '}
              {/* <FiInfo className="text-[#919191] text-xs" title="Question info" /> */}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => updateQuestion(q.id, { showExplanation: !q.showExplanation })}
            className="text-xs text-[#919191] hover:text-[#0077b5] flex items-center gap-1 flex-shrink-0"
          >
            {q.showExplanation ? 'Hide explanation' : 'Show explanation'}
            {q.showExplanation ? <FiChevronUp className="text-xs" /> : <FiChevronDown className="text-xs" />}
          </button>
        </div>
      </div>

      {/* ─── Side-by-side layout: Question + Upload panel ─── */}
      <div className={`flex flex-col ${q.showUploadPanel ? 'md:flex-row md:items-start' : ''} gap-60 mt-2`}>
        {/* Left side: question text + options */}
        <div className="flex-shrink-0">
          <div className="flex items-start gap-2">
            {!isReviewed && (
              <input
                type="checkbox"
                checked={q.selected}
                onChange={() => toggleSelect(q.id)}
                className="w-4 h-4 mt-0.5 accent-[#F47F2E] cursor-pointer flex-shrink-0"
              />
            )}
            <p className={`text-sm font-semibold ${q.selected ? 'text-[#F47F2E]' : 'text-[#0077b5]'}`}>
              {q.text}
            </p>
          </div>

          <div className="mt-3 ml-6 space-y-1.5">
            {q.options.map((opt, idx) => {
              const isSelected = q.pendingAnswer !== null ? q.pendingAnswer === idx : q.selectedOption === idx;
              const isPending = q.pendingAnswer === idx;
              const isMatchedOption = matchedOptionIdx === idx && matchType && matchType !== "No Match";

              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer text-sm text-[#53545a]">
                  <input
                    type="radio"
                    name={`option-${q.id}`}
                    checked={isSelected || isMatchedOption}
                    onChange={() => handleOptionChange(q.id, idx)}
                    className="accent-[#0077b5]"
                    disabled={isReviewed}
                  />
                  {opt}
                  {isPending && (
                    <span className="text-[10px] text-[#F47F2E] font-medium ml-1">(pending)</span>
                  )}
                  {isMatchedOption && !isSelected && (
                    <span className="text-[10px] text-green-600 font-medium ml-1">(crosswalk)</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        {/* Upload Panel: sits right beside the question */}
        {/* 
         q.showUploadPanel && (
          <div className="w-full md:w-[260px] flex-shrink-0 bg-[#f7f8fa] border border-[#e0e2e6] rounded-xl p-1 sm:p-2 relative transition-all self-center">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-[#53545a]">
                Upload Supporting Document
              </h4>
              <button
                onClick={() => handleCloseUploadPanel(q.id)}
                className="p-1.5 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors"
                title="Close"
              >
                <FiX className="text-sm" />
              </button>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#919191] rounded-xl p-2 sm:p-3 cursor-pointer hover:border-[#0077b5] transition-colors mb-2">
              <FiPaperclip className="text-lg text-[#0077b5] mb-0.5" />
              <span className="text-xs font-medium text-[#53545a]">
                {uploadFile ? uploadFile.name : 'Upload File'}
              </span>
              <input
                type="file"
                className="hidden"
                onChange={(e) =>
                  setUploadFile(e.target.files?.[0] || null)
                }
              />
            </label>

            <textarea
              value={q.pendingReason || ''}
              onChange={(e) => updateQuestion(q.id, { pendingReason: e.target.value })}
              placeholder="Write the reason here..."
              className="w-full border border-[#919191] rounded-xl p-2 text-xs text-[#53545a] placeholder-[#919191] outline-none focus:border-[#0077b5] resize-none h-16"
            />
          </div>
         )
        */}
      </div>

      <div className="mt-3 ml-6">
        <p className="text-sm font-semibold text-[#F47F2E] mb-1.5">Confidence</p>
        <div className="flex flex-wrap gap-3">
          {confidenceLevels.map((level) => {
            const isActive = q.pendingConfidence !== null ? q.pendingConfidence === level : q.confidence === level;
            const isPending = q.pendingConfidence === level;
            return (
              <label key={level} className="flex items-center gap-1.5 cursor-pointer text-sm text-[#53545a]">
                <input
                  type="radio"
                  name={`confidence-${q.id}`}
                  checked={isActive}
                  onChange={() => handleConfidenceChange(q.id, level)}
                  className="accent-[#0077b5]"
                  disabled={isReviewed}
                />
                {level}
                {isPending && (
                  <span className="text-[10px] text-[#F47F2E] font-medium ml-0.5">(pending)</span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-4 ml-6 flex items-center justify-between">
        <div className="flex gap-2">
          {isReviewed ? (
            <button onClick={() => handleUndo(q.id)} className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors">
              Undo
            </button>
          ) : (
            <>
              {

                <>
                  <button onClick={() => handleApprove(q.id)} className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Approve
                  </button>
                  <button onClick={() => handleReject(q.id)} className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors">
                    Reject
                  </button>
                </>

              }

              {hasPendingChanges && (
                <button
                  onClick={() => handleSaveAnswer(q.id)}
                  disabled={q.saving}
                  className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors disabled:opacity-50"
                >
                  {q.saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </>
          )}
        </div>
        {isApproved && (
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full border-4 border-[#0077b5] flex items-center justify-center rotate-[-15deg]">
              <p className="text-[8px] font-bold text-[#0077b5] uppercase tracking-wide">Approved</p>
            </div>
          </div>
        )}
        {isRejected && (
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full border-4 border-[#F47F2E] flex items-center justify-center rotate-[15deg]">
              <p className="text-[8px] font-bold text-[#F47F2E] uppercase tracking-wide">Rejected</p>
            </div>
          </div>
        )}
      </div>

      {/* Explanation Section with smooth animation */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${q.showExplanation ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
          }`}
      >
        <div className="border-t border-[#919191]/20 pt-4">
        {
          rec && (
               <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-[#53545a]">Review</h4>
          
                   <div className="flex items-center gap-2">
             
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
              >
                {showDetails ? 'Hide Details' : 'View Full Details →'}
              </button>
              <button onClick={() => updateQuestion(q.id, { showExplanation: !q.showExplanation })} className="p-1.5 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors" title="Close explanation">
                <FiX className="text-sm" />
              </button>
            </div>
            
            
          </div>
          )
        }
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#ffffff] rounded-xl p-4">
              {
                rec && (
                    <div className="flex items-center gap-2 mb-2">
                <FiFileText className="text-[#919191]" />
                <span className="text-xs font-semibold text-[#919191]">Rationale & Cross-Walk</span>
                
              </div>
                )
              }
              
             {
              rec &&(
                 <p className="text-xs text-[#53545a] leading-tight mb-3">{rec?.rationale}</p>
              )
             } 

              {/* Source Info Section - Clean & Simple */}

              {
                rec ? (
                   <div className="mb-4 bg-white border border-[#e0e2e6] overflow-hidden">
                {/* Header */}
                <div className="bg-[#0077b5] px-4 py-2">
                  <p className="text-xs font-semibold text-white text-center">Source Mapping</p>
                </div>
                
                {/* Content - Simple Table Layout */}
                <div className="divide-y divide-[#e0e2e6]">
                  {/* Assessment Row */}
                  <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                    <div className="w-28 flex-shrink-0">
                      <span className="text-[11px] font-semibold text-[#919191] uppercase">Assessment</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#53545a] font-medium">
                        {sourceAnswer?.find(sa => sa?.question_id === rec?.source_question_id)?.assessment_description || '—'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Questionnaire Row */}
                  <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                    <div className="w-28 flex-shrink-0">
                      <span className="text-[11px] font-semibold text-[#919191] uppercase">Questionnaire</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#53545a] font-medium">
                       
                        {sourceAnswer?.find(sa => sa?.question_id === rec?.source_question_id)?.questionnaire_name || '—'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Question Row */}
                  <div className="flex items-start px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                    <div className="w-28 flex-shrink-0 pt-0.5">
                      <span className="text-[11px] font-semibold text-[#919191] uppercase">Question</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#53545a] font-medium leading-relaxed">
                        {sourceAnswer?.find(sa => sa?.question_id === rec?.source_question_id)?.question_text || '—'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Answer Row - Highlighted */}
                  <div className={`flex items-start px-4 py-3 border-l-4 ${matchType === 'No Match' ? 'bg-[#FFEBEE] border-[#F44336]' : 'bg-[#E8F5E9] border-[#4CAF50]'}`}>
                    <div className="w-28 flex-shrink-0 pt-0.5">
                      <span className={`text-[11px] font-semibold uppercase ${matchType === 'No Match' ? 'text-[#C62828]' : 'text-[#2E7D32]'}`}>Answer</span>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold leading-relaxed ${matchType === 'No Match' ? 'text-[#C62828]' : 'text-[#2E7D32]'}`}>
                        {sourceAns?.answer_text || '—'}
                        {matchType && (
                          <span className={`text-xs font-semibold ml-2 ${matchType === 'No Match' ? 'text-[#F44336]' : 'text-[#4CAF50]'}`}>
                            {`(${matchType})`}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Evidence Row */}
                  {(q.additionalInfo || q.additionalInfoUrl) && (
                    <div className="flex items-center px-4 py-3 hover:bg-[#f7f8fa] transition-colors">
                      <div className="w-28 flex-shrink-0">
                        <span className="text-[11px] font-semibold text-[#919191] uppercase">Evidence -</span>
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        {q.additionalInfo && (
                          <span className="text-sm text-[#53545a]">{q.additionalInfo}</span>
                        )}
                        {q.additionalInfoUrl && (
                          <button
                            onClick={handleOpenDocument}
                            title={`Open: ${q.additionalInfoUrl.split('/').pop()}`}
                            className="text-[#0077b5] hover:text-[#005a8f] transition-colors flex-shrink-0"
                          >
                            <FiFile className="text-lg" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
                ):(
                  <div className="flex items-center gap-3 p-3">
                    <FiAlertCircle className="text-[#F47F2E] flex-shrink-0" size={20} />
                    <p className="text-sm font-medium text-[#53545a]">No cross-walk match found. Please select an answer manually.</p>
                  </div>
                )
              }
              

          
             
       
            </div>
            <div className="bg-[#ffffff] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiFileText className="text-[#919191]" />
                <span className="text-xs font-semibold text-[#919191]">Reviewer Notes</span>
              </div>
              <textarea
                value={q.reviewerNotes}
                onChange={(e) => updateQuestion(q.id, { reviewerNotes: e.target.value })}
                placeholder="write a note..."
                className="w-full bg-white border border-[#919191] p-2 text-xs text-[#919191] placeholder-[#919191] outline-none focus:border-[#0077b5] resize-none h-[85%]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Review Panel - floats on top of card content on the right */}
      <div
        className={`absolute top-0 right-0 h-full w-full sm:w-[280px] md:w-[320px] bg-white border-l border-[#919191] shadow-lg p-3 sm:p-4 transition-transform duration-300 ease-in-out ${showDetails && q.showExplanation ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#53545a]">Detailed Review</h4>
          <button onClick={() => setShowDetails(false)} className="p-1 rounded-lg hover:bg-[#919191]/10 text-[#919191] transition-colors">
            <FiX className="text-sm" />
          </button>
        </div>
        {/* <p className="text-xs text-[#919191] mb-4 leading-relaxed">{q.detailedReview.description}</p> */}
        <div className="flex flex-wrap gap-1.5 mb-2 sm:mb-3">
          {[ 'Cross-Walk', 'Provenance','Audit'].map((tab) => (
            <button
              key={tab}
              onClick={() => setDetailTab(tab)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-semibold transition-colors ${detailTab === tab ? 'bg-[#F47F2E] text-white' : 'bg-[#919191]/10 text-[#919191] hover:bg-[#919191]/20 border border-[#919191]'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="text-xs text-[#919191] h-[calc(100%-80px)] overflow-hidden">
          {detailTab === 'Provenance' && (
            <>
         
              <div><p className="font-semibold text-[#53545a] mb-1">Provenance Example:</p>
             </div>
            

            </>
          )}
          {detailTab === 'Cross-Walk' && <div className="h-full flex flex-col"><p className="font-semibold text-[#53545a] mb-1.5 flex-shrink-0">Cross-Walk Details:</p> 
            <style>{`
              .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: #0077b5 #f7f8fa;
              }
              .custom-scrollbar::-webkit-scrollbar {
                width: 8px;
              }
              .custom-scrollbar::-webkit-scrollbar-track {
                background: #f7f8fa;
                border-radius: 10px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, #0077b5 0%, #005a8f 100%);
                border-radius: 10px;
                border: 2px solid #f7f8fa;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(180deg, #005a8f 0%, #003d5c 100%);
              }
            `}</style>
            {rec && (() => {
              const matchingSource = sourceAnswer?.find(sa => sa?.question_id === rec?.source_question_id);
              
              return (
                <div className="custom-scrollbar bg-[#f7f8fa] border border-[#e0e2e6] p-2 flex-1 overflow-y-auto min-h-0">
                  {/* Display structured rationale_details if available */}
                  {rec?.rationale_details && (
                    <div className="space-y-2">

                      {rec.rationale_details.source_evidence && (
                        <div className="bg-white p-2 border border-[#e0e2e6]">
                          <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">Source Evidence:</p>
                          <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">{rec.rationale_details.source_evidence}</p>
                        </div>
                      )}

                      {rec.rationale_details.control_alignment && (
                        <div className="bg-white p-2 border border-[#e0e2e6]">
                          <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">Control Objective Alignment:</p>
                          <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">{rec.rationale_details.control_alignment}</p>
                        </div>
                      )}

                      {rec.rationale_details.target_need && (
                        <div className="bg-white  p-2 border border-[#e0e2e6]">
                          <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">Target Need:</p>
                          <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">{rec.rationale_details.target_need}</p>
                        </div>
                      )}

                      {rec.rationale_details.org_relevance && (
                        <div className="bg-white p-2 border border-[#e0e2e6]">
                          <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">Organization Relevance:</p>
                          <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">{rec.rationale_details.org_relevance}</p>
                        </div>
                      )}

                      {rec.rationale_details.caveats && (
                        <div className="bg-white p-2 border border-[#e0e2e6]">
                          <p className="text-[10px] sm:text-xs font-semibold text-[#0077b5] mb-1">Caveats:</p>
                          <p className="text-[10px] sm:text-xs text-[#53545a] leading-relaxed">{rec.rationale_details.caveats}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                </div>
              );
            })()}
          </div>}
          {detailTab === 'Audit' && <div><p className="font-semibold text-[#53545a] mb-1">Audit Trail:</p><p>{q.detailedReview.audit}</p></div>}
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;
